<template>
  <polling-channel
    :input="places"
    :active="pollingActive"
    :updateFunction="pollPlaceUpdate"
    :interval="1000"
    @valueUpdated="onPlacePollingUpdate"
  >
    <template v-slot="props">
      <div>
        <page-title-area>
          <h1>S-TYPE</h1>
          <template #side-content>
            <entity-search-field
              v-model="searchText"
              :placeholder="$t('search')"
            />
          </template>
          <template #right-floating-content>
            <station-plan-status />
          </template>
        </page-title-area>
        <div class="d-flex align-baseline justify-end mt-4">
          <template v-if="featureIsActive('s_type_scanner_upload')">
            <round-button icon="camera_alt" @click="openScannerUpload">
              {{ $t('scanner_upload.button') }}
            </round-button>
            <span class="ml-1" />
          </template>
          <csv-import-button
            @placesCreated="loadPlaces"
          />
        </div>
        <station-place-table
          :places="props.currentValue"
          :loading="loading"
          :users="users"
          :current-user-id="currentUserId"
          :query="query"
          @edit="editPlace"
          @delete="confirmDelete"
          @toggleHidden="toggleHidden"
        />
        <confirm-dialog
          v-model="deleteConfirmDialogVisible"
          :message="$t('confirm_delete', { placeName: deleteTarget.name })"
          @yes="deletePlace(deleteTarget)"
        />
        <station-place-editor
          v-model="editorVisible"
          @update="updateTable"
          :place="editTarget"
          :users="users"
        />
        <scanner-upload-dialog
          ref="scannerUploadDialog"
          v-model="scannerUploadDialogVisible"
          :places="props.currentValue"
          @uploaded="onScannerUpload"
        />
      </div>
    </template>
  </polling-channel>
</template>

<i18n lang="yaml">
ja:
  search: 文字列で絞り込む
  confirm_delete: '"{placeName}"を削除します。よろしいですか？'
  delete_success: '"{placeName}"を削除しました。'
  scanner_upload:
    button: '画像アップロード'
    success: '画像解析のアップロードが完了しました'
  messages:
    failed_to_load_organization_users: 組織ユーザーの読み込みに失敗しました

en:
  search: Filter by text
  confirm_delete: 'This will delete "{placeName}". Are you sure?'
  delete_success: '"{placeName}" was deleted.'
  scanner_upload:
    button: 'Image Upload'
    success: 'Image analysis upload completed successfully'
  messages:
    failed_to_load_organization_users: Failed to load organization users
</i18n>

<script>
import { mapActions, mapGetters } from 'vuex';
import Features from '@/mixins/features';
import Notifications from '@/mixins/notifications';

import ConfirmDialog from '@/components/atoms/ConfirmDialog';
import EntitySearchField from '@/components/atoms/EntitySearchField';
import PageTitleArea from '@/components/atoms/PageTitleArea';
import PollingChannel from '@/components/atoms/PollingChannel';
import RoundButton from '@/components/atoms/RoundButton';

import CsvImportButton from './CsvImportButton';
import StationPlaceEditor from './StationPlaceEditor';
import StationPlaceTable from './StationPlaceTable';
import StationPlanStatus from './StationPlanStatus';
import ScannerUploadDialog from '../StationPlaceEvents/ScannerUploadDialog';

const STATION_PLACE_QUERY_STORAGE_KEY = 'stationPlaceQuery';

export default {
  name: 'station-places',
  mixins: [
    Features,
    Notifications,
  ],
  components: {
    ConfirmDialog,
    CsvImportButton,
    EntitySearchField,
    PageTitleArea,
    PollingChannel,
    RoundButton,
    ScannerUploadDialog,
    StationPlaceEditor,
    StationPlaceTable,
    StationPlanStatus,
  },
  data() {
    const currentUserId = this.$store.getters.getCurrentUser.id;
    const allStoredQueries = this.localObjectStorage.load(STATION_PLACE_QUERY_STORAGE_KEY) || {};
    const storedQuery = allStoredQueries[currentUserId] || {};

    return {
      places: [],
      users: [],
      loading: true,
      deleteConfirmDialogVisible: false,
      deleteTarget: { name: '' },
      editorVisible: false,
      editTarget: null,
      searchText: storedQuery.searchText || '',
      storeQueryAfterFinishingTyping: this._.debounce(this.storeQuery, 300),
      scannerUploadDialogVisible: false,
      pollingPlaceId: null,
      pollingActive: false,
      pollingCount: 0,
    };
  },
  computed: {
    ...mapGetters(['getOrganizationUsers']),
    currentUserId() {
      return this.$store.getters.getCurrentUser?.id;
    },
    query() {
      return {
        searchText: this.searchText,
      };
    },
  },
  inject: [
    'api',
    'localObjectStorage',
  ],
  mounted: async function () {
    await this.loadPlaces();
    await this.loadUsers();
  },
  methods: {
    ...mapActions(['requestOrganizationUsers']),
    confirmDelete(place) {
      this.deleteTarget = place;
      this.deleteConfirmDialogVisible = true;
    },
    deletePlace: async function (place) {
      this.places = this.places.filter(p => p.id !== place.id);
      this.notifySuccess('delete_success', { placeName: place.name });
      await this.api.deleteStationPlace(place.id);
    },
    editPlace(place) {
      this.editTarget = place;
      this.editorVisible = true;
    },
    loadPlaces: async function () {
      this.loading = true;
      this.places = await this.api.fetchStationPlaces();
      this.loading = false;
    },
    loadUsers: async function () {
      try {
        const currentUser = this.$store.getters.getCurrentUser;
        if (currentUser.owner_id) {
          this.users = await this.requestOrganizationUsers(currentUser.owner_id);
        }
      } catch (error) {
        this.notifyError('messages.failed_to_load_organization_users');
        this.users = [];
      }
    },
    toggleHidden(place) {
      const newValue = !place.hidden;
      this.updateTable({ ...place, hidden: newValue });
      this.api.updateStationPlace(place.id, { hidden: newValue });
    },
    updateTable(place) {
      this.places = this.places.map((p) => {
        if (p.id === place.id) {
          return place;
        }

        return p;
      });
    },
    openScannerUpload() {
      this.scannerUploadDialogVisible = true;
    },
    onScannerUpload({ event }) {
      // ポーリング開始
      this.pollingPlaceId = event.place_id;
      this.pollingCount = 0;
      this.pollingActive = true;
    },
    isPlaceUploadTimeUpdated(targetPlace) {
      if (!targetPlace || !targetPlace.last_upload_time) {
        return false;
      }

      const originalPlace = this.places.find(p => p.id === this.pollingPlaceId);
      return targetPlace.last_upload_time !== originalPlace?.last_upload_time;
    },
    async pollPlaceUpdate() {
      const places = await this.api.fetchStationPlaces({ isBackgroundRequest: true });
      const targetPlace = places.find(p => p.id === this.pollingPlaceId);

      this.pollingCount += 1;

      // 最終アップロード日が更新されていればポーリング停止
      if (this.isPlaceUploadTimeUpdated(targetPlace)) {
        this.pollingActive = false;
      }

      // ポーリング回数が5回に達したら停止
      if (this.pollingCount >= 5) {
        this.pollingActive = false;
      }

      return places;
    },
    onPlacePollingUpdate(updatedPlaces) {
      this.places = updatedPlaces;

      // ポーリング完了時にダイアログを閉じて通知
      if (!this.pollingActive) {
        this.$refs.scannerUploadDialog.close();
        this.notifySuccess('scanner_upload.success');
      }
    },
    storeQuery() {
      const currentUser = this.$store.getters.getCurrentUser;
      const allStoredQueries = this.localObjectStorage.load(STATION_PLACE_QUERY_STORAGE_KEY) || {};
      this.localObjectStorage.store(STATION_PLACE_QUERY_STORAGE_KEY, {
        ...allStoredQueries,
        [currentUser.id]: { searchText: this.searchText },
      });
    },
  },
  provide() {
    return {
      getStationPlaces: () => this.places,
    };
  },
  watch: {
    editorVisible(value) {
      if (!value) {
        this.editTarget = null;
      }
    },
    searchText() {
      this.storeQueryAfterFinishingTyping();
    },
  },
};
</script>

<style scoped lang="sass">
</style>
